import React, { useContext, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import is from 'is_js';
import MainLayout from './MainLayout';
import EmbedLayout from './EmbedLayout';
import SettingsToggle from 'components/settings-panel/SettingsToggle';
import SettingsPanel from 'components/settings-panel/SettingsPanel';

import ErrorLayout from './ErrorLayout';
import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';

import Landing from 'components/pages/landing/Landing';
import ArticleDetail from 'y4/components/pages/article/topic/ArticleDetail';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/common/Toast';

import CardLogin from 'components/authentication/card/Login';
import Logout from 'components/authentication/card/Logout';
import CardRegistration from 'components/authentication/card/Registration';
import CardForgetPassword from 'components/authentication/card/ForgetPassword';
import CardConfirmMail from 'components/authentication/card/ConfirmMail';
import CardPasswordReset from 'components/authentication/card/PasswordReset';
import CardLockScreen from 'components/authentication/card/LockScreen';

import Invoice from 'components/app/e-commerce/Invoice';
import Billing from 'components/app/e-commerce/billing/Billing';
import Checkout from 'components/app/e-commerce/checkout/Checkout';
import ShoppingCart from 'components/app/e-commerce/cart/ShoppingCart';
import CustomersDetails from 'components/app/e-commerce/customers-details/CustomersDetails';
import OrderDetails from 'components/app/e-commerce/orders/order-details/OrderDetails';
import Products from 'components/app/e-commerce/product/Products';
import ProductDetails from 'components/app/e-commerce/product/product-details/ProductDetails';
import Orders from 'components/app/e-commerce/orders/order-list/Orders';
import Customers from 'components/app/e-commerce/customers/Customers';
import ProductElement from 'y4/ProductElement';
import ProductWeiXinElement from 'y4/ProductWeiXinElement';

import Chat from 'components/app/chat/Chat';

import AppContext from 'context/Context';

import Profile from 'y4/components/pages/user/profile/Profile';

import Blank from 'y4/layouts/content/Blank';
import ProtectedElement from 'y4/ProtectedElement';
import AuthenticationElement from 'y4/AuthenticationElement';
import Home from 'y4/layouts/content/Home';
import DataContext from 'y4/context/DataContext';
import { ModalContainer } from 'y4/components/modal/ModalContainer';
import MainContent from 'y4/layouts/content/MainContent';
import _ from 'lodash';

import Icons from 'y4/layouts/content/Icons';
import Shortener from 'y4/layouts/content/Shortener';
import ToastList from 'y4/layouts/content/ToastList';
import { getRoutePath } from 'y4/Utils';

const Layout = props => {
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition, corp }
  } = useContext(AppContext);

  useEffect(() => {
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    if (is.safari()) {
      HTMLClassList.add('safari');
    }
  }, [HTMLClassList]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  //There is unprotected page route
  let unprotected = [];
  //Union route in props
  unprotected = _.union(unprotected, props.routes);
  //Union route embed page
  const location = useLocation();
  const reg = new RegExp('/\\w+/content/\\w+/embed');
  const reg_result = reg.exec(location.pathname);
  if (reg_result) {
    unprotected = _.union(unprotected, [reg_result[0]]);
  }
  //Get users page route that save in globalData after login
  const { globalData } = useContext(DataContext);
  const userRoutes = globalData.route;

  return (
    <>
      <Routes>
        <Route path="y4/icons" element={<Icons />} />
        <Route path=":corp?/" element={<Home />} />
        <Route path=":corp?/s/:id" element={<Shortener />} />
        <Route path=":corp/landing" element={<Landing />} />
        {/* Error */}
        <Route element={<ErrorLayout />}>
          <Route path={`${corp}/errors/404`} element={<Error404 />} />
          <Route path={`${corp}/errors/500`} element={<Error500 />} />
        </Route>
        {/* Embed page */}
        <Route element={<EmbedLayout />}>
          {unprotected.map((route, index) => {
            return (
              <Route
                key={index}
                path={getRoutePath(corp, route)}
                element={<MainContent key={`unprotected${index}`} />}
              />
            );
          })}
        </Route>
        {/* Authentication */}
        <Route element={<AuthenticationElement />}>
          {/* Card */}
          <Route
            path=":corp/authentication/card/login"
            element={<CardLogin />}
          />
          <Route
            path=":corp/authentication/card/register"
            element={<CardRegistration />}
          />
          <Route path=":corp/authentication/card/logout" element={<Logout />} />
          <Route
            path=":corp/authentication/card/forgot-password"
            element={<CardForgetPassword />}
          />
          <Route
            path=":corp/authentication/card/reset-password"
            element={<CardPasswordReset />}
          />
          <Route
            path=":corp/authentication/card/confirm-mail"
            element={<CardConfirmMail />}
          />
          <Route
            path=":corp/authentication/card/lock-screen"
            element={<CardLockScreen />}
          />
        </Route>
        {/* MainLayout */}
        <Route element={<MainLayout />}>
          <Route path=":corp/toasts" element={<ToastList />} />
          {/* E Commerce */}
          <Route element={<ProductElement />}>
            <Route element={<ProtectedElement />}>
              <Route path=":corp/e-commerce/checkout" element={<Checkout />} />
              <Route
                path=":corp/e-commerce/shopping-cart"
                element={<ShoppingCart />}
              />
              <Route
                path=":corp/e-commerce/orders/order-details/:id"
                element={<OrderDetails />}
              />
              <Route
                path=":corp/e-commerce/orders/order-list"
                element={<Orders />}
              />
            </Route>
            <Route path=":corp/e-commerce/invoice" element={<Invoice />} />
            <Route path=":corp/e-commerce/billing" element={<Billing />} />
            <Route path=":corp/e-commerce/customers" element={<Customers />} />
            <Route
              path=":corp/e-commerce/customer-details"
              element={<CustomersDetails />}
            />
            <Route element={<ProductWeiXinElement />}>
              <Route
                path=":corp/e-commerce/product/product-details"
                element={<ProductDetails />}
              />
              <Route
                path=":corp/e-commerce/product/product-details/:productId"
                element={<ProductDetails />}
              />
              <Route
                path=":corp/e-commerce/product/:productLayout"
                element={<Products />}
              />
            </Route>
          </Route>
          {/* ProtectedElement */}
          <Route element={<ProtectedElement />}>
            {/* ProtectedElement */}
            <Route path=":corp/ai/chat/:session" element={<Chat />} />
            <Route path=":corp/article" element={<ArticleDetail />} />
            <Route path=":corp/content/blank" element={<Blank />} />
            {/*Inherent routes */}
            <Route path=":corp/user/profile" element={<Profile />} />
            {/*Y4 routes*/}
            {userRoutes &&
              userRoutes.map((route, index) => {
                return (
                  <Route
                    key={index}
                    path={getRoutePath(corp, route)}
                    element={<MainContent key={`protected${index}`} />}
                  />
                );
              })}
          </Route>
        </Route>
        {/* Other path */}
        <Route
          path="*"
          element={
            <Navigate
              to={`${corp}/errors/404?referrer=${location.pathname}`}
              replace
            />
          }
        />
      </Routes>
      {reg_result ? null : (
        <>
          <SettingsToggle />
          <SettingsPanel />
        </>
      )}
      <ToastContainer
        closeButton={CloseButton}
        icon={false}
        draggable={false}
        position={toast.POSITION.BOTTOM_LEFT}
      />
      <ModalContainer />
    </>
  );
};

Layout.propTypes = {
  routes: PropTypes.array
};

export default Layout;
